import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Btn, Dot, I } from "./ui";
import type { DeviceDTO, EventDTO, ProvinceDTO } from "../api/contract";
import {
  ApiRequestError,
  getEvents,
  getSparkline,
  listDevices,
  listProvinces,
} from "./api";
import { subscribeWs } from "./ws";
import { Tweaks, type TweakState } from "./Tweaks";
import { Dashboard } from "./screens/Dashboard";
import { Guida } from "./screens/Guida";
import { Extension } from "./screens/Extension";
import { AddressLookup } from "./screens/AddressLookup";
import { Necrologi } from "./screens/Necrologi";
import { Prospects } from "./screens/Prospects";
import { Sources } from "./screens/Sources";
import { Users } from "./screens/Users";
import { Organizations } from "./screens/Organizations";
import { AppShell } from "./app/AppShell";
import { useDemoScope } from "./useDemoScope";
import {
  organization,
  useActiveMemberRole,
  useActiveOrganization,
  useListOrganizations,
  useSession,
} from "./auth-client";
import { SPARK_LEN, sparkPointsFrom } from "./app/metrics";
import {
  routeArgsFromLocation,
  routeFromLocation,
  routeTarget,
  type Route,
  type RouteArgs,
} from "./app/routes";
import {
  applyTweaksToDocument,
  parseStoredTweaks,
  TWEAK_STORAGE_KEY,
} from "./app/tweaks";
import { isPlatformAdminRole } from "../api/platform-roles";

interface Toast {
  id: number;
  kind: "ok" | "err";
  msg: string;
}

export function App() {
  const { data: activeOrg, isPending } = useActiveOrganization();
  if (isPending) {
    return <div className="login-wrap" role="status">Caricamento organizzazione…</div>;
  }
  return <OrganizationApp key={activeOrg?.id ?? "no-organization"} />;
}

function OrganizationApp() {
  const { data: userSession } = useSession();
  const { data: activeOrg } = useActiveOrganization();
  const { data: orgs } = useListOrganizations();
  const { data: memberRole, isPending: memberRolePending } = useActiveMemberRole();
  const demoScope = useDemoScope();
  const isAdmin = isPlatformAdminRole(userSession?.user?.role);
  const orgRole = memberRole?.role;
  const canManageOrg = isAdmin || (!demoScope && (orgRole === "owner" || orgRole === "admin"));
  const [autoOrgTried, setAutoOrgTried] = useState(false);
  const [route, setRoute] = useState<Route>(() => routeFromLocation());
  const [routeArgs, setRouteArgs] = useState<RouteArgs>(() =>
    routeArgsFromLocation()
  );
  const [devices, setDevices] = useState<DeviceDTO[]>([]);
  const [events, setEvents] = useState<EventDTO[]>([]);
  const [provinces, setProvinces] = useState<ProvinceDTO[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [tweaks, setTweaks] = useState<TweakState>(() => {
    const next = parseStoredTweaks(localStorage.getItem(TWEAK_STORAGE_KEY));
    applyTweaksToDocument(next);
    return next;
  });
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [spark, setSpark] = useState<number[]>([]);
  const activeRoute = !isAdmin && (route === "users" || (route === "orgs" && !memberRolePending && !canManageOrg))
    ? "dashboard"
    : route;

  // Sync URL when route/args change
  useEffect(() => {
    const target = routeTarget(activeRoute, activeRoute === route ? routeArgs : {});
    const current = window.location.pathname + window.location.search;
    if (target !== current) {
      window.history.pushState({}, "", target);
    }
  }, [activeRoute, route, routeArgs]);

  // Gestisce back/forward del browser
  useEffect(() => {
    const onPop = () => {
      setRoute(routeFromLocation());
      setRouteArgs(routeArgsFromLocation());
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    applyTweaksToDocument(tweaks);
    localStorage.setItem(TWEAK_STORAGE_KEY, JSON.stringify(tweaks));
  }, [tweaks]);

  // tweaks protocol — Claude Design edit-mode handshake
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = (e.data || {}) as { type?: string };
      if (d.type === "__activate_edit_mode") setTweaksOpen(true);
      if (d.type === "__deactivate_edit_mode") setTweaksOpen(false);
    };
    window.addEventListener("message", onMsg);
    window.parent?.postMessage({ type: "__edit_mode_available" }, "*");
    return () => window.removeEventListener("message", onMsg);
  }, []);
  useEffect(() => {
    window.parent?.postMessage({ type: "__edit_mode_set_keys", edits: tweaks }, "*");
  }, [tweaks]);

  // Initial parallel load
  const loadInitial = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [d, e, p, sp] = await Promise.all([
        listDevices(),
        getEvents(50),
        listProvinces(),
        getSparkline(60),
      ]);
      setDevices(d);
      setEvents(e);
      setProvinces(p);
      setSpark(sparkPointsFrom(sp));
      setLoading(false);
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : "Backend non raggiungibile";
      setLoadError(msg);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  // Sessioni vecchie possono non avere un'org attiva (creata prima dell'hook
  // di login): se l'utente appartiene ad almeno un'organizzazione, selezionala
  // automaticamente e ricarica i dati, così non resta bloccato su "Nessuna org.".
  useEffect(() => {
    if (autoOrgTried) return;
    if (activeOrg) return;
    if (!orgs || orgs.length === 0) return;
    setAutoOrgTried(true);
    void organization
      .setActive({ organizationId: orgs[0].id })
      .then(({ error }) => {
        if (error) setLoadError(error.message ?? "Impossibile selezionare l'organizzazione");
      })
      .catch(() => setLoadError("Impossibile selezionare l'organizzazione"));
  }, [autoOrgTried, activeOrg, orgs]);

  // WebSocket subscription — only after a successful load
  useEffect(() => {
    if (loadError) return;
    if (loading) return;
    const unsubscribe = subscribeWs({
      onConnect: () => setWsConnected(true),
      onDisconnect: () => setWsConnected(false),
      onDeviceState: (device) => {
        setDevices((prev) => {
          const idx = prev.findIndex((d) => d.id === device.id);
          if (idx === -1) return [device, ...prev];
          const next = prev.slice();
          next[idx] = device;
          return next;
        });
      },
      onDeviceRevoked: (deviceId) => {
        setDevices((prev) => prev.map((d) => (d.id === deviceId ? { ...d, revokedAt: new Date().toISOString(), online: false } : d)));
      },
      onEvent: (event) => {
        setEvents((prev) => [event, ...prev].slice(0, 50));
      },
      onMetricTick: ({ reqsPerMin }) => {
        setSpark((prev) => {
          const next = prev.slice();
          if (next.length >= SPARK_LEN) next.shift();
          next.push(reqsPerMin);
          return next;
        });
      },
    });
    return unsubscribe;
  }, [loading, loadError]);

  // Refresh devices periodically (online state changes after 60s of no activity)
  useEffect(() => {
    if (loading || loadError) return;
    const handle = setInterval(() => {
      void listDevices().then(setDevices).catch(() => undefined);
    }, 30_000);
    return () => clearInterval(handle);
  }, [loading, loadError]);

  const go = (name: string, args: RouteArgs = {}) => {
    setRoute(name as Route);
    setRouteArgs(args);
  };

  const pushToast = (kind: "ok" | "err", msg: string) => {
    const id = Math.random();
    setToasts((t) => [...t, { id, kind, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  };

  const onlineCount = devices.filter((d) => d.online && !d.revokedAt).length;

  let content: ReactNode = null;
  if (activeRoute === "dashboard") content = <Dashboard devices={devices} liveEvents={events} spark={spark} onGo={go} memberView={!isAdmin} />;
  else if (activeRoute === "guida") content = <Guida onGo={go} />;
  else if (activeRoute === "extension") content = <Extension pushToast={pushToast} openPairing={routeArgs.abbina === "1"} />;
  else if (activeRoute === "lookup") content = <AddressLookup provinces={provinces} pushToast={pushToast} />;
  else if (activeRoute === "necrologi") content = <Necrologi key={String(routeArgs.cerca ?? "")} provinces={provinces} defaultScope={demoScope} openSearchPicker={routeArgs.cerca === "1"} />;
  else if (activeRoute === "prospects") content = <Prospects key={String(routeArgs.vista ?? "")} provinces={provinces} defaultProvincia={demoScope?.provincia} initialView={typeof routeArgs.vista === "string" ? routeArgs.vista : undefined} />;
  else if (activeRoute === "sources") content = <Sources pushToast={pushToast} isAdmin={isAdmin} />;
  else if (activeRoute === "users") content = <Users pushToast={pushToast} />;
  else if (activeRoute === "orgs") content = <Organizations pushToast={pushToast} />;

  return (
    <>
      <AppShell
        route={activeRoute}
        isAdmin={isAdmin}
        canManageOrg={canManageOrg}
        onlineCount={onlineCount}
        loading={loading}
        wsConnected={wsConnected}
        onGo={go}
        theme={tweaks.mode}
        onToggleTheme={() => setTweaks((t) => ({ ...t, mode: t.mode === "dark" ? "light" : "dark" }))}
      >
          {loadError ? (
            <div
              className="preview-box"
              style={{
                background: "var(--danger-dim)",
                borderColor: "var(--danger-line-strong)",
                color: "var(--danger)",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <strong>Backend non raggiungibile</strong>
              <span style={{ flex: 1, fontSize: 12 }}>{loadError}</span>
              <Btn size="sm" variant="primary" icon={I.refresh} onClick={() => void loadInitial()}>Riprova</Btn>
            </div>
          ) : (
            content
          )}
      </AppShell>

      {/* toasts */}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            <Dot kind={t.kind === "ok" ? "live" : "danger"} />
            <span>{t.msg}</span>
          </div>
        ))}
      </div>

      {tweaksOpen && <Tweaks tweaks={tweaks} setTweaks={setTweaks} onClose={() => setTweaksOpen(false)} />}
    </>
  );
}
